<script setup>
  import { defineProps, defineEmits, toRaw, ref, computed, defineExpose } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    dynamicSearchStandalone,
    categorySearchTrigger,
    debounce
  } from '../helpers/CatalogPublicationFiltersHelper';
  import { useCatalogPublicationCategoriesStore } from '../../stores/CatalogPublicationCategoriesStore';
  import { useCatalogPublicationLanguagesStore } from '../../stores/CatalogPublicationLanguagesStore';
  import { useCatalogPublicationAuthorsStore } from '../../stores/CatalogPublicationAuthorsStore';
  import { useCatalogPublicationImprintsStore } from '../../stores/CatalogPublicationImprintsStore';
  import { useCatalogPublicationCollectionsStore } from '../../stores/CatalogPublicationCollectionsStore.js';
  import { useCatalogPublicationLibrariesStore } from '../../stores/CatalogPublicationLibrariesStore';
  import { useCatalogPublicationLibraryStore } from '../../stores/CatalogPublicationLibraryStore';
  import { useCatalogPublishingGroupsStore } from '../../stores/CatalogPublishingGroupsStore';
  import { useCatalogSeriesStore } from '../../stores/CatalogSeriesStore';
  import { useCatalogListsStore } from '../../stores/CatalogListsStore';
  import { useCatalogAwardsStore } from '../../stores/CatalogAwardsStore';

  const { t } = useI18n();

  const languagesStore = useCatalogPublicationLanguagesStore();
  const categoriesStore = useCatalogPublicationCategoriesStore();
  const authorsStore = useCatalogPublicationAuthorsStore();
  const imprintsStore = useCatalogPublicationImprintsStore();
  const collectionsStore = useCatalogPublicationCollectionsStore();
  const librariesStore = useCatalogPublicationLibrariesStore();
  const publishingGroupsStore = useCatalogPublishingGroupsStore();
  const seriesStore = useCatalogSeriesStore();
  const listsStore = useCatalogListsStore();
  const awardsStore = useCatalogAwardsStore();

  const props = defineProps({
    title: { type: String, default: '' },
    readableTile: { type: String, default: '' },
    id: { type: String, default: '' },
    selectedId: { type: String, default: '' },
    fetcher: { type: String, default: '' },
    loadedOptionsProperty: { type: String, default: '' },
    mapper: { type: Function, default: null },
    lang: { type: String, default: '' }
  });

  const emit = defineEmits(['optionClick', 'clearField']);

  const loadedOptions = ref([]);
  const inputFocused = ref(false);

  const options = computed(() => {
    const raw = loadedOptions.value;
    if (props.title === 'libraries')
      return (Array.isArray(raw) ? raw : []).map(o => ({ label: o.attributes.name, id: o.id }));
    if (props.title === 'language')
      return (Array.isArray(raw) ? raw : []).map(o => ({ label: `${o.name} (${o.nameOriginal})`, id: o.alpha2 }));
    if (props.title === 'category')
      return (raw?.categories || []).map(o => ({ label: o.map, inputValue: o.name, id: o.code }));
    if (props.title === 'author')
      return (Array.isArray(raw) ? raw : []).map(o => ({ label: o.nameOriginal, id: o.id }));
    if (props.title === 'collection')
      return (Array.isArray(raw) ? raw : []).map(o => ({ label: o.attributes.original_name, id: o.id }));
    return (Array.isArray(raw) ? raw : []).map(o => ({ label: o.name, id: o.id }));
  });

  const showDropdown = computed(() => inputFocused.value && options.value.length > 0);

  const setSelected = async (selector, value, name) => {
    const input = window.document.getElementById(selector);
    if (!input) return;
    if (selector == 'categorySelector') {
      const categories = await categoriesStore.fetchCategories(value);
      if (categories) {
        const categoryAttr = categories.data[0].attributes;
        input.value = categoryAttr[`name_${props.lang}`];
      }
    }
    if (selector == 'librarySelector') input.value = name;
    if (selector == 'imprintSelector') {
      const imprints = await imprintsStore.fetchImprint(value);
      if (imprints) input.value = imprints.data.attributes.name;
    }
    if (selector == 'languageSelector') {
      const languages = await languagesStore.fetchLanguage(value);
      if (!languages) return;
      const translatedLang = props.lang == 'en' ? languages.data.name : languages.data[`name_${props.lang}`];
      input.value = translatedLang;
    }
    if (selector == 'authorSelector') {
      const authors = await authorsStore.fetchAuthor(value);
      if (authors) input.value = authors.data.attributes.fullname;
    }
    if (selector == 'collectionsSelector') {
      const collections = await collectionsStore.fetchCollection(value);
      if (collections) input.value = collections.data.attributes.original_name;
    }
    if (selector == 'publishingGroupSelector') {
      const publishingGroup = await publishingGroupsStore.fetchPublishingGroup(value);
      if (publishingGroup) input.value = publishingGroup.data.data.attributes.name;
    }
    if (selector == 'serieSelector') {
      const serie = await seriesStore.fetchSerie(value);
      if (serie) input.value = serie.data.data.attributes.original_name;
    }
    if (selector == 'listSelector') {
      const list = await listsStore.fetchList(value);
      if (list) input.value = list.data.data.attributes.name;
    }
    if (selector == 'awardSelector') {
      const award = await awardsStore.fetchAward(value);
      if (award) input.value = award.data.data.attributes.name;
    }
  }
  defineExpose({ setSelected })

  const getFetcher = () => {
    if (props.title === 'language') return languagesStore;
    if (props.title === 'author') return authorsStore;
    if (props.title === 'imprint') return imprintsStore;
    if (props.title === 'collection') return collectionsStore;
    if (props.title === 'libraries') return librariesStore;
    if (props.title === 'publishing_group') return publishingGroupsStore;
    if (props.title === 'serie') return seriesStore;
    if (props.title === 'list') return listsStore;
    if (props.title === 'award') return awardsStore;
  }

  const search = async (event) => {
    if (props.title === 'category') {
      categorySearch(event);
    } else {
      dynamicSearchStandalone(
        event,
        loadedOptions,
        getFetcher(),
        toRaw,
        props.fetcher,
        props.lang,
        props.mapper,
        t,
        props.title
      );
    }
  }

  const selectOption = (option) => {
    const input = document.getElementById(props.id);
    if (input) input.value = option.inputValue || option.label;
    inputFocused.value = false;
    emit('optionClick', null, props.id, option.id);
  }

  const clearField = (field, inputId, isDate) => {
    loadedOptions.value = [];
    inputFocused.value = false;
    emit('clearField', field, inputId, isDate);
  }

  const categorySearch = async (event) => {
    if (event.inputType === 'insertFromPaste') {
      categorySearchTrigger(event.target.value, props.lang, toRaw, loadedOptions, categoriesStore, props.title);
    } else if (event.inputType === 'insertText' || event.inputType === 'deleteContentBackward') {
      debounce(event.target.value).then(async (valid) => {
        if (!valid) return;
        categorySearchTrigger(event.target.value, props.lang, toRaw, loadedOptions, categoriesStore, props.title);
      });
    }
  }

  const getCorrectProperty = () => {
    switch (props.selectedId) {
      case 'category':      return 'category_ids';
      case 'author':        return 'author_ids';
      case 'imprint':       return 'imprint_ids';
      case 'publishing_group': return 'publishing_group_id';
      case 'collection':    return 'collection_ids';
      case 'library_id':    return 'library_id';
      case 'lang':          return 'languages';
      default:              return props.title;
    }
  }

</script>

<template>
  <div class="input-group position-relative">
    <input
      class="form-control"
      :placeholder="`&#128270; &nbsp; ${t(`components.catalog_publication.filters.${props.title}`)}`"
      v-on:input="search"
      @focus="inputFocused = true"
      @blur="inputFocused = false"
      :id="props.id">
    <ul v-if="showDropdown" class="dropdown-menu show" style="width: max-content; min-width: 100%;" @mousedown.prevent>
      <li v-for="option in options" :key="option.id ?? option.label">
        <a class="dropdown-item" @click="selectOption(option)">{{ option.label }}</a>
      </li>
    </ul>
    <span
      class="input-group-text"
      @click="clearField(getCorrectProperty(), props.id, false)"
    ><span :id="`${props.title}_loader`" class="not_found"></span>
      x
    </span>
  </div>
</template>
